import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fileToBase64, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, Clock, Users, Building2, Handshake, Mic2,
  Briefcase, LayoutGrid, Upload, ExternalLink, RefreshCcw, Trash2, FileText, Plus, X,
} from "lucide-react";

/**
 * Display America Command Center — event-day operations dashboard.
 * Admins/tl_internal only. Consolidates registration, production, stations, checklist,
 * staff assignments, live schedule, and event documents.
 */
const CHECKLIST_KEYS = [
  "registration_ready", "graphics_installed", "electrical_complete",
  "networking_area_ready", "food_beverage_ready", "av_ready",
  "speaker_ready", "staff_ready",
];

const STAFF_ROLES = [
  "registration", "supplier_support", "operations",
  "speaker_support", "executive_matchmaking",
];

const DOC_KINDS = [
  ["agenda", "Agenda"],
  ["venue_map", "Venue Map"],
  ["parking", "Parking"],
  ["wifi", "Wi-Fi"],
  ["emergency_contacts", "Emergency Contacts"],
  ["supplier_guide", "Supplier Guide"],
];

const STATUS_STYLES = {
  green:  { bg: "bg-emerald-500", ring: "border-emerald-500", chip: "text-emerald-700 bg-emerald-50" },
  yellow: { bg: "bg-amber-400",   ring: "border-amber-400",   chip: "text-amber-700 bg-amber-50"   },
  red:    { bg: "bg-red-500",     ring: "border-red-500",     chip: "text-red-700 bg-red-50"       },
};

export default function CommandCenter() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const tickRef = useRef(0);

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) { nav("/login"); return; }
    load();
    const poll = setInterval(load, 30000);
    return () => clearInterval(poll);
  }, [user, loading]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/command-center");
      setData(data);
    } catch { toast.error("Failed to load Command Center."); }
    finally { setLoadingData(false); }
  };

  // Local tick to drive countdowns without hammering the API
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => { tickRef.current += 1; setNow(Date.now()); }, 1000);
    return () => clearInterval(t);
  }, []);

  const updateChecklist = async (key, status) => {
    try {
      await api.put("/admin/checklist", { key, status });
      setData((d) => d ? { ...d, checklist: d.checklist.map((c) => c.key === key ? { ...c, status } : c) } : d);
    } catch { toast.error("Could not save."); }
  };

  const updateStaff = async (role, assignees) => {
    try {
      await api.put("/admin/staff-assignments", { role, assignees });
      setData((d) => d ? { ...d, staff: d.staff.map((s) => s.role === role ? { ...s, assignees } : s) } : d);
    } catch { toast.error("Could not save."); }
  };

  if (loading || loadingData) return <Layout><section className="container-summit py-24"><div className="overline">Loading Command Center…</div></section></Layout>;
  if (!data) return null;

  const { event, registration, production, stations, checklist, staff, schedule, documents } = data;

  return (
    <Layout>
      {/* Header */}
      <section className="bg-[#0A0A0A] text-white border-b border-white/10">
        <div className="container-summit py-10 md:py-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="overline text-white/60"><span className="accent-rule" />Display America · Command Center</div>
            <h1 className="serif text-4xl sm:text-5xl mt-3">{event.name}</h1>
            <p className="mt-3 text-sm text-white/70">
              {event.date} · {event.opening_time} – {event.closing_time} · {event.location}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={load} className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white"
                    data-testid="cc-refresh">
              <RefreshCcw size={12} /> Refresh
            </button>
            <Link to="/checkin" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="cc-link-checkin">
              Check-In →
            </Link>
            <Link to="/admin/program" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="cc-link-program">
              Program Tracker →
            </Link>
            <Link to="/event-settings" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="cc-link-settings">
              Event Settings →
            </Link>
            <Link to="/admin" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="cc-link-admin">
              Admin →
            </Link>
          </div>
        </div>
      </section>

      <section className="container-summit py-10 grid gap-10">
        {/* Row 1 — Registration KPIs */}
        <RegistrationKpis reg={registration} />

        {/* Row 2 — Schedule + Countdowns */}
        <ScheduleRow schedule={schedule} />

        <div className="grid lg:grid-cols-[3fr_2fr] gap-8">
          {/* Row 3 (col A) — Operational Checklist */}
          <ChecklistPanel items={checklist} onChange={updateChecklist} />

          {/* Row 3 (col B) — Station Summary */}
          <StationSummary stations={stations} />
        </div>

        <div className="grid lg:grid-cols-[3fr_2fr] gap-8">
          {/* Row 4 (col A) — Supplier Production */}
          <ProductionPanel prod={production} />

          {/* Row 4 (col B) — Staff Assignments */}
          <StaffPanel staff={staff} onSave={updateStaff} />
        </div>

        {/* Row 5 — Event Documents */}
        <DocumentsPanel documents={documents} onChanged={load} />
      </section>
    </Layout>
  );
}

/* ---------- Registration KPIs ---------- */
function RegistrationKpis({ reg }) {
  const items = [
    { key: "prime_supplier", label: "Prime Suppliers", value: reg.prime_supplier, icon: Building2 },
    { key: "sbe",            label: "SBE",             value: reg.sbe,             icon: Handshake },
    { key: "tl_attendee",    label: "Travel + Leisure",value: reg.tl_attendee,     icon: Briefcase },
    { key: "speaker",        label: "Speakers",        value: reg.speaker,         icon: Mic2 },
    { key: "checked_in",     label: "Checked In",      value: reg.checked_in,      icon: CheckCircle2, accent: true },
    { key: "remaining",      label: "Remaining",       value: reg.remaining,       icon: Clock },
  ];
  return (
    <div data-testid="cc-registration-kpis">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="overline"><span className="accent-rule" />Registration</div>
          <div className="serif text-2xl mt-2">Attendee readiness</div>
        </div>
        <div className="text-xs text-zinc-500 tabular-nums">Total registered <span className="text-black font-medium">{reg.total}</span></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[hsl(var(--line))]">
        {items.map((it) => (
          <div key={it.key} data-testid={`cc-kpi-${it.key}`}
               className={`bg-white p-5 ${it.accent ? "border-l-2 border-[#A39171]" : ""}`}>
            <div className="flex items-center gap-2 text-zinc-400"><it.icon size={14} strokeWidth={1.4} /></div>
            <div className="serif text-3xl mt-2 tabular-nums">{it.value ?? 0}</div>
            <div className="overline mt-1">{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Schedule ---------- */
function ScheduleRow({ schedule }) {
  const cur = schedule.current, nxt = schedule.upcoming;
  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };
  return (
    <div className="grid md:grid-cols-4 gap-px bg-[hsl(var(--line))]" data-testid="cc-schedule">
      <div className="bg-white p-5 md:col-span-2">
        <div className="overline">Current Session</div>
        {cur ? (
          <>
            <div className="serif text-xl mt-2 leading-tight" data-testid="cc-current-title">{cur.title}</div>
            <div className="text-xs text-zinc-500 mt-1">{fmt(cur.start)} – {fmt(cur.end)} · {cur.location || "TBA"}</div>
          </>
        ) : (
          <div className="serif text-xl mt-2 text-zinc-400">No session in progress</div>
        )}
      </div>
      <div className="bg-white p-5 md:col-span-2">
        <div className="overline">Next Session</div>
        {nxt ? (
          <>
            <div className="serif text-xl mt-2 leading-tight" data-testid="cc-next-title">{nxt.title}</div>
            <div className="text-xs text-zinc-500 mt-1">{fmt(nxt.start)} – {fmt(nxt.end)} · {nxt.location || "TBA"}</div>
          </>
        ) : (
          <div className="serif text-xl mt-2 text-zinc-400">To Be Announced</div>
        )}
      </div>
      <Countdown label="Countdown to Lunch" seconds={schedule.countdown_to_lunch_seconds}
                 fallback={schedule.lunch_time_label} testId="cc-countdown-lunch" />
      <Countdown label="Countdown to Supplier Showcase" seconds={schedule.countdown_to_showcase_seconds}
                 fallback={schedule.showcase_time_label} testId="cc-countdown-showcase" />
    </div>
  );
}

function Countdown({ label, seconds, fallback, testId }) {
  // Fixed at first render; the parent tick drives re-renders so we compute a live remaining
  const [displaySec, setDisplaySec] = useState(seconds ?? null);
  useEffect(() => {
    setDisplaySec(seconds ?? null);
    if (seconds == null) return;
    const start = Date.now();
    const initial = seconds;
    const t = setInterval(() => {
      const remaining = initial - Math.floor((Date.now() - start) / 1000);
      setDisplaySec(remaining);
      if (remaining <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [seconds]);

  let content;
  if (displaySec == null) content = <span className="text-zinc-400">{fallback || "TBA"}</span>;
  else if (displaySec <= 0) content = <span className="text-emerald-700">Live now</span>;
  else if (displaySec >= 86400) {
    const days = Math.floor(displaySec / 86400);
    const hrs = Math.floor((displaySec % 86400) / 3600);
    content = <span className="tabular-nums">{days}d {String(hrs).padStart(2,"0")}h</span>;
  }
  else {
    const h = Math.floor(displaySec / 3600);
    const m = Math.floor((displaySec % 3600) / 60);
    const s = displaySec % 60;
    content = <span className="tabular-nums">{h > 0 ? `${String(h).padStart(2,"0")}:` : ""}{String(m).padStart(2,"0")}:{String(s).padStart(2,"0")}</span>;
  }
  return (
    <div className="bg-white p-5" data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="serif text-3xl mt-2 leading-tight">{content}</div>
    </div>
  );
}

/* ---------- Operational Checklist ---------- */
function ChecklistPanel({ items, onChange }) {
  return (
    <div data-testid="cc-checklist">
      <div className="overline mb-4"><span className="accent-rule" />Operational Checklist</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[hsl(var(--line))]">
        {items.map((it) => (
          <div key={it.key} className="bg-white p-5 flex items-center gap-4" data-testid={`cc-checklist-${it.key}`}>
            <div className={`h-3 w-3 rounded-full shrink-0 ${STATUS_STYLES[it.status]?.bg || "bg-zinc-300"}`} />
            <div className="flex-1">
              <div className="text-sm font-medium leading-tight">{it.label}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">{it.status}</div>
            </div>
            <div className="flex gap-1">
              {["red", "yellow", "green"].map((s) => (
                <button key={s} onClick={() => onChange(it.key, s)}
                        data-testid={`cc-checklist-${it.key}-${s}`}
                        aria-label={`Set ${it.label} to ${s}`}
                        className={`h-6 w-6 rounded-full border ${STATUS_STYLES[s].ring} ${it.status === s ? STATUS_STYLES[s].bg : "bg-white hover:bg-zinc-50"}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Station Summary ---------- */
function StationSummary({ stations }) {
  const rows = [
    ["Available",  stations.available, "text-emerald-700"],
    ["Reserved",   stations.reserved,  "text-amber-700"],
    ["Occupied",   stations.occupied,  "text-black"],
    ["Checked In", stations.checked_in,"text-[#A39171]"],
  ];
  return (
    <div className="card-flat p-6" data-testid="cc-stations">
      <div className="flex items-baseline justify-between mb-4">
        <div className="overline">Supplier Stations</div>
        <div className="text-xs text-zinc-500 tabular-nums">Total <span className="text-black font-medium">{stations.total}</span></div>
      </div>
      <ul className="grid gap-3">
        {rows.map(([label, value, color]) => (
          <li key={label} className="flex items-baseline justify-between py-3 border-b border-zinc-100 last:border-0" data-testid={`cc-station-${label.toLowerCase().replace(" ","-")}`}>
            <span className="text-sm text-zinc-700 inline-flex items-center gap-2"><LayoutGrid size={14} className="text-zinc-400" /> {label}</span>
            <span className={`serif text-2xl tabular-nums ${color}`}>{value ?? 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Production ---------- */
function ProductionPanel({ prod }) {
  const stages = [
    ["logo_received",       "Logo Received"],
    ["graphics_in_design",  "Graphics In Design"],
    ["proof_approved",      "Proof Approved"],
    ["production_complete", "Production Complete"],
    ["ready_for_event",     "Ready for Event"],
  ];
  return (
    <div data-testid="cc-production">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline"><span className="accent-rule" />Supplier Production Status</div>
          <div className="text-xs text-zinc-500 mt-2">{prod.total_prime} Prime Supplier{prod.total_prime === 1 ? "" : "s"} tracked</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[hsl(var(--line))]">
        {stages.map(([k, label]) => {
          const val = prod[k] ?? 0;
          const pct = prod.total_prime ? Math.round((val / prod.total_prime) * 100) : 0;
          return (
            <div key={k} className="bg-white p-5" data-testid={`cc-production-${k}`}>
              <div className="serif text-3xl tabular-nums">{val}</div>
              <div className="overline mt-2">{label}</div>
              <div className="mt-3 h-1 bg-zinc-100">
                <div className="h-full bg-[#A39171]" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-2 tabular-nums">{pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Staff Assignments ---------- */
function StaffPanel({ staff, onSave }) {
  return (
    <div className="card-flat p-6" data-testid="cc-staff">
      <div className="overline mb-4">Staff Assignments</div>
      <div className="grid gap-4">
        {staff.map((row) => (
          <StaffRow key={row.role} row={row} onSave={onSave} />
        ))}
      </div>
    </div>
  );
}

function StaffRow({ row, onSave }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    onSave(row.role, [...row.assignees, v]);
    setInput("");
  };
  const remove = (name) => onSave(row.role, row.assignees.filter((n) => n !== name));
  return (
    <div className="border-b border-zinc-100 pb-4 last:border-0 last:pb-0" data-testid={`cc-staff-${row.role}`}>
      <div className="text-sm font-medium">{row.label}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {row.assignees.map((n) => (
          <span key={n} className="inline-flex items-center gap-1 border border-zinc-300 px-3 py-1 text-xs">
            <Users size={11} className="text-zinc-400" /> {n}
            <button onClick={() => remove(n)} className="ml-1 text-zinc-400 hover:text-black" aria-label={`Remove ${n}`}
                    data-testid={`cc-staff-${row.role}-remove-${n}`}>
              <X size={11} />
            </button>
          </span>
        ))}
        {row.assignees.length === 0 && <span className="text-xs text-zinc-400">Unassigned</span>}
      </div>
      <div className="mt-3 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
               placeholder="Add staff name"
               data-testid={`cc-staff-${row.role}-input`}
               className="h-9 rounded-none flex-1" />
        <button onClick={add} className="h-9 px-3 border border-zinc-300 text-xs uppercase tracking-[0.18em] hover:border-black inline-flex items-center gap-1"
                data-testid={`cc-staff-${row.role}-add`}>
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}

/* ---------- Event Documents ---------- */
function DocumentsPanel({ documents, onChanged }) {
  const byKind = useMemo(() => {
    const map = {};
    for (const d of documents || []) (map[d.kind] = map[d.kind] || []).push(d);
    return map;
  }, [documents]);

  return (
    <div data-testid="cc-documents">
      <div className="flex items-baseline justify-between mb-4">
        <div className="overline"><span className="accent-rule" />Event Documents</div>
        <div className="text-xs text-zinc-500">{documents.length} uploaded</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {DOC_KINDS.map(([kind, label]) => (
          <DocSlot key={kind} kind={kind} label={label} docs={byKind[kind] || []} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function DocSlot({ kind, label, docs, onChanged }) {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const base64 = await fileToBase64(file);
      const { data: up } = await api.post("/uploads", {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        data_base64: base64,
        purpose: `event_doc_${kind}`,
      });
      await api.post("/admin/event-documents", {
        kind, filename: file.name, file_id: up.file_id, label,
      });
      toast.success(`${label} uploaded.`);
      onChanged();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Upload failed.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/admin/event-documents/${id}`);
      toast.success("Document removed.");
      onChanged();
    } catch { toast.error("Delete failed."); }
  };

  return (
    <div className="border border-zinc-200 p-5" data-testid={`cc-doc-slot-${kind}`}>
      <div className="flex items-start justify-between">
        <div className="text-sm font-medium">{label}</div>
        <FileText size={14} className="text-[#A39171]" strokeWidth={1.4} />
      </div>
      <div className="mt-3 grid gap-2">
        {docs.length === 0 && <div className="text-xs text-zinc-400">Not uploaded</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 justify-between border-t border-zinc-100 pt-2 first:border-0 first:pt-0"
               data-testid={`cc-doc-${d.id}`}>
            <a href={`${BACKEND_URL}/api/files/${d.file_id}`} target="_blank" rel="noreferrer"
               className="text-xs underline underline-offset-4 truncate flex-1">
              {d.filename}
            </a>
            <a href={`${BACKEND_URL}/api/files/${d.file_id}`} target="_blank" rel="noreferrer"
               className="text-zinc-400 hover:text-black shrink-0" aria-label="Open">
              <ExternalLink size={12} />
            </a>
            <button onClick={() => remove(d.id)} className="text-zinc-400 hover:text-red-700 shrink-0"
                    data-testid={`cc-doc-remove-${d.id}`} aria-label="Remove">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
      <label className="mt-4 btn-outline-black h-9 text-xs w-full cursor-pointer" data-testid={`cc-doc-upload-${kind}`}>
        <Upload size={12} /> {busy ? "Uploading…" : docs.length ? "Add another" : "Upload"}
        <input type="file" hidden onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );
}
